'use client';

import type { CallInsight, Emotion, TranscriptSegment } from '../../engine/protocol';
import { useEngineClient, useEngineTranscript } from '../lib/engineSdk';
import type { EngineClient } from '../lib/engineClient';

/**
 * Legenda ao vivo e leitura da conversa durante a chamada (só no modo motor).
 * Mostra as últimas falas (parciais em itálico, corrigidas na mesma linha), as
 * emoções detectadas e, quando existe, a leitura contextual: postura do cliente,
 * interesse e a próxima jogada sugerida. Fora do modo motor não renderiza nada.
 */
const EMOTION_LABEL: Record<string, string> = {
  raiva: 'raiva',
  irritacao: 'irritação',
  frustracao: 'frustração',
  desconfianca: 'desconfiança',
  impaciencia: 'impaciência',
  confusao: 'confusão',
  nervosismo: 'nervosismo',
  surpresa: 'surpresa',
  desanimo: 'desânimo',
  cansaco: 'cansaço',
  interesse: 'interesse',
  entusiasmo: 'entusiasmo',
  satisfacao: 'satisfação',
  alivio: 'alívio',
  neutro: 'neutro',
};

const STANCE_LABEL: Record<CallInsight['customer']['stance'], string> = {
  aberto: 'cliente aberto',
  neutro: 'cliente neutro',
  resistente: 'cliente resistente',
  hostil: 'cliente hostil',
  indefinido: 'lendo a conversa',
};

const SPEAKER_LABEL: Record<TranscriptSegment['speaker'], string> = { agent: 'Você', customer: 'Cliente', mixed: '' };

export function topEmotions(segment: TranscriptSegment, max = 2): Emotion[] {
  return (segment.emotions ?? []).filter((e) => e.label !== 'neutro' && e.intensity >= 0.3).slice(0, max);
}

export interface LiveCaptionPanelProps {
  /** quantas falas ficam visíveis (as últimas) */
  maxLines?: number;
}

export function LiveCaptionPanel({ maxLines = 4 }: LiveCaptionPanelProps) {
  const client = useEngineClient();
  if (!client) return null;
  return <LiveCaptionPanelInner client={client} maxLines={maxLines} />;
}

function LiveCaptionPanelInner({ client, maxLines }: { client: EngineClient; maxLines: number }) {
  const { segments, insight, status } = useEngineTranscript(client);
  const listening = status?.live === 'running';
  if (segments.length === 0 && !insight && !listening) return null;
  const rows = segments.slice(-maxLines);
  return (
    <div className="live-captions" id="liveCaptions" aria-live="polite">
      {insight && (
        <div className={`live-insight stance-${insight.customer.stance}`} id="liveInsight" title={insight.summary}>
          <span className="live-insight-stance">{STANCE_LABEL[insight.customer.stance] ?? insight.customer.stance}</span>
          <span className="live-insight-interest">interesse {Math.round(insight.customer.interest)}%</span>
          {insight.nextMove && <span className="live-insight-next">{insight.nextMove}</span>}
        </div>
      )}
      {rows.length > 0 ? (
        <ul className="live-caption-list">
          {rows.map((segment) => (
            <li key={segment.id} className={`live-caption ${segment.speaker}${segment.interim ? ' interim' : ''}${segment.refined ? ' refined' : ''}`}>
              <span className="live-caption-speaker">{SPEAKER_LABEL[segment.speaker]}</span>
              <span className="live-caption-text">{segment.text}</span>
              {topEmotions(segment).map((emotion) => (
                <span key={emotion.label} className={`live-caption-emotion${emotion.source === 'context' ? ' ctx' : ''}`} title={emotion.evidence ?? ''}>
                  {EMOTION_LABEL[emotion.label] ?? emotion.label} {Math.round(emotion.intensity * 100)}%
                </span>
              ))}
            </li>
          ))}
        </ul>
      ) : (
        <div className="live-caption-waiting">Ouvindo a conversa…</div>
      )}
    </div>
  );
}
